import type { MouseEvent } from "react";

// Unified switch primitive. Two modes:
//
//   <Switch checked={x} onChange={f} ariaLabel="…" />
//     bare track — for in-row boolean settings (notifications, debug
//     toggles, Match's Agentic AI master switch).
//
//   <Switch checked={x} onChange={f} ariaLabel="…"
//           label={{ on: "Teilen", off: "Privat" }} />
//     labeled pill — for sharing-style toggles where the affordance
//     carries its own text (Profile entries, AddForm forms). Click
//     bubbling is stopped automatically because labeled switches
//     typically live inside click-to-expand rows.
//
// `exiting` is only meaningful with `label` — it swaps the mount-in
// animation for a fade-out. The caller keeps the element mounted for
// ~200ms so the animation can finish.

interface SwitchLabel {
  on: string;
  off: string;
  // "active" (default) shows only the word that currently applies.
  // "pair" shows both words with the knob on the active side. A single
  // alternating word can be read either as the current state or as the
  // action the click performs, so the pair spells the choice out. Reserve
  // it for the switches that govern a whole group (the profile's "Alle:"
  // master and the per-section masters in the box headers); on a dense row
  // of switches the extra width costs more than the clarity buys.
  display?: "active" | "pair";
}

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
  label?: SwitchLabel;
  disabled?: boolean;
  exiting?: boolean;
  stopPropagation?: boolean;
  className?: string;
  title?: string;
  // Optional DOM id set on the <button> — a stable, locale-independent hook
  // for e2e tests (spark-validation), mirroring Select's `id`.
  id?: string;
}

export function Switch({
  checked,
  onChange,
  ariaLabel,
  label,
  disabled = false,
  exiting = false,
  stopPropagation = false,
  className = "",
  title,
  id,
}: SwitchProps) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation || label) e.stopPropagation();
    onChange(!checked);
  };

  if (label) {
    const pair = label.display === "pair";
    const word = "text-smycles-micro font-semibold tracking-[0.16em] whitespace-nowrap transition-colors duration-200 ease-in-out";
    return (
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        title={title}
        onClick={handleClick}
        className={`${exiting ? "toggle-pop-out" : "toggle-pop"} inline-flex items-center gap-1.5 rounded-full px-1.5 py-0.5 ring-1 transition-colors duration-200 ease-in-out disabled:opacity-60 ${
          checked
            ? `bg-accent/10 ring-accent-decoration/30 ${pair ? "" : "text-ink-accent"}`
            : `bg-surface ring-ink/10 ${pair ? "" : "text-ink-muted hover:text-ink-muted"}`
        } ${className}`}
      >
        {pair ? (
          <span className={`${word} ${checked ? "text-ink-accent" : "text-ink-muted"}`}>{label.on}</span>
        ) : (
          <span className="w-10 text-center text-smycles-micro font-semibold tracking-[0.16em]">
            {checked ? label.on : label.off}
          </span>
        )}
        <span
          className={`relative h-3.5 w-6 shrink-0 rounded-full transition-colors duration-200 ease-in-out ${
            checked ? "bg-accent" : pair ? "bg-ink/20" : "bg-ink/10"
          }`}
        >
          <span
            className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-surface shadow-sm transition-all duration-200 ease-in-out ${
              // In pair mode the knob points at the word that applies, so it
              // sits left when on; the single-word pill keeps the familiar
              // off-left / on-right reading.
              (pair ? !checked : checked) ? "left-3" : "left-0.5"
            }`}
          />
        </span>
        {pair ? (
          <span className={`${word} ${checked ? "text-ink-muted" : "text-ink"}`}>{label.off}</span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      title={title}
      onClick={handleClick}
      className={`relative h-6 w-10 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
        checked ? "bg-accent" : "bg-ink/20"
      } ${className}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-page shadow transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </button>
  );
}
